import React from 'react'
import { Box, Card, CardContent, Typography, Chip, Stack } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import BuildIcon from '@mui/icons-material/Build'
import ApiIcon from '@mui/icons-material/Api'
import WebIcon from '@mui/icons-material/Web'
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid'
import { ISystemUptimeEvent, ESystemStatus } from '../graphql'
import { statusColors } from '../theme'
import dayjs from 'dayjs'

interface UptimeStatusCardProps {
  uptimeEvent: ISystemUptimeEvent
}

const getStatusIcon = (status: ESystemStatus) => {
  switch (status) {
    case ESystemStatus.OPERATIONAL:
      return <CheckCircleIcon sx={{ fontSize: 48 }} />
    case ESystemStatus.DEGRADED:
      return <WarningIcon sx={{ fontSize: 48 }} />
    case ESystemStatus.MAINTENANCE:
      return <BuildIcon sx={{ fontSize: 48 }} />
    case ESystemStatus.OUTAGE:
      return <ErrorIcon sx={{ fontSize: 48 }} />
  }
}

const getStatusLabel = (status: ESystemStatus): string => {
  switch (status) {
    case ESystemStatus.OPERATIONAL:
      return 'All Systems Operational'
    case ESystemStatus.DEGRADED:
      return 'Degraded Performance'
    case ESystemStatus.MAINTENANCE:
      return 'Scheduled Maintenance'
    case ESystemStatus.OUTAGE:
      return 'System Outage'
  }
}

export const UptimeStatusCard: React.FC<UptimeStatusCardProps> = ({ uptimeEvent }) => {
  const statusColor = statusColors[uptimeEvent.status]

  return (
    <Card
      sx={{
        borderLeft: 6,
        borderColor: statusColor,
        backgroundColor: 'background.paper',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Box sx={{ color: statusColor }}>{getStatusIcon(uptimeEvent.status)}</Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ color: statusColor, mb: 1 }}>
              {getStatusLabel(uptimeEvent.status)}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
                Last uptime event description:
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {uptimeEvent.description}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {uptimeEvent.apiAffected && (
                <Chip
                  icon={<ApiIcon />}
                  label="API Affected"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
              {uptimeEvent.webappAffected && (
                <Chip
                  icon={<WebIcon />}
                  label="Web App Affected"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
              {uptimeEvent.mobileAffected && (
                <Chip
                  icon={<PhoneAndroidIcon />}
                  label="Mobile Affected"
                  size="small"
                  color="error"
                  variant="outlined"
                />
              )}
              {uptimeEvent.reloadRequired && (
                <Chip label="Reload Required" size="small" color="warning" variant="filled" />
              )}
              {uptimeEvent.expectedConclusion && (
                <Typography variant="caption" color="text.secondary">
                  Expected Resolution:{' '}
                  {dayjs(uptimeEvent.expectedConclusion).format('MMM D, YYYY h:mm A')}
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
